import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
  Shield,
  AlertCircle,
  Minus,
  Info,
  Play,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("academiaos_token");

// ── Status configs ─────────────────────────────────────────────────────────────

const EVAL_STATUS = {
  NOT_EVALUATED: { label: "Not Evaluated", color: "text-muted-foreground", bg: "bg-muted/50", border: "border-border", icon: Minus },
  IN_PROGRESS:   { label: "In Progress",   color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-amber-200 dark:border-amber-800", icon: Clock },
  BLOCKED:       { label: "Blocked",       color: "text-red-600 dark:text-red-400", bg: "bg-red-50 dark:bg-red-950/30", border: "border-red-200 dark:border-red-800", icon: XCircle },
  READY:         { label: "Ready",         color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200 dark:border-emerald-800", icon: CheckCircle2 },
};

const REQ_STATUS = {
  PASS:        { label: "Pass",        color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-950/30", icon: CheckCircle2 },
  FAIL:        { label: "Fail",        color: "text-red-600 dark:text-red-400", bg: "bg-red-50 dark:bg-red-950/30", icon: XCircle },
  IN_PROGRESS: { label: "In Progress", color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-950/30", icon: Clock },
  WARN:        { label: "Warning",     color: "text-orange-500 dark:text-orange-400", bg: "bg-orange-50 dark:bg-orange-950/30", icon: AlertTriangle },
  SKIP:        { label: "Skip",        color: "text-muted-foreground", bg: "bg-muted/50", icon: Minus },
};

const LIFECYCLE_STATUS = {
  OPEN:       { label: "Open",      color: "text-sky-600 dark:text-sky-400", bg: "bg-sky-50 dark:bg-sky-950/30", border: "border-sky-200 dark:border-sky-800" },
  VALIDATED:  { label: "Validated", color: "text-violet-600 dark:text-violet-400", bg: "bg-violet-50 dark:bg-violet-950/30", border: "border-violet-200 dark:border-violet-800" },
  APPROVED:   { label: "Approved",  color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200 dark:border-emerald-800" },
  CLOSED:     { label: "Closed",    color: "text-muted-foreground", bg: "bg-muted/50", border: "border-border" },
};

// Lifecycle transitions and their action labels
const LIFECYCLE_ACTIONS = {
  OPEN:      [{ to: "VALIDATED", label: "Validate Gate", requiresReady: true, style: "primary" }, { to: "CLOSED", label: "Close Gate", requiresReady: false, style: "danger" }],
  VALIDATED: [{ to: "APPROVED", label: "Approve Gate", requiresReady: false, style: "primary" }, { to: "OPEN", label: "Reopen Gate", requiresReady: false, style: "secondary" }, { to: "CLOSED", label: "Close Gate", requiresReady: false, style: "danger" }],
  APPROVED:  [{ to: "CLOSED", label: "Close Gate", requiresReady: false, style: "danger" }, { to: "OPEN", label: "Reopen Gate", requiresReady: false, style: "secondary" }],
  CLOSED:    [],
};

// ── Sub-components ────────────────────────────────────────────────────────────

function EvalStatusBanner({ gate }) {
  const evalCfg = EVAL_STATUS[gate.latest_evaluation_status] || EVAL_STATUS.NOT_EVALUATED;
  const EvalIcon = evalCfg.icon;

  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border ${evalCfg.bg} ${evalCfg.border} mb-1`} data-testid="gate-eval-banner">
      <EvalIcon className={`h-5 w-5 shrink-0 mt-0.5 ${evalCfg.color}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold ${evalCfg.color}`}>{evalCfg.label}</span>
          {gate.is_stale && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-700">
              <AlertCircle className="h-3 w-3" /> Re-evaluation required
            </span>
          )}
        </div>
        {gate.is_stale && gate.stale_reason && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">{gate.stale_reason}</p>
        )}
        {gate.last_evaluated_at && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Last evaluated: {new Date(gate.last_evaluated_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}

function RequirementRow({ req, idx }) {
  const cfg = REQ_STATUS[req.status] || REQ_STATUS.SKIP;
  const Icon = cfg.icon;
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      data-testid={`req-row-${idx}`}
      className={`rounded-lg border p-3 mb-2 ${cfg.bg} border-opacity-50`}
      style={{ borderColor: req.status === "FAIL" ? "rgb(254,202,202)" : req.status === "PASS" ? "rgb(187,247,208)" : undefined }}
    >
      <div className="flex items-start gap-3">
        <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${cfg.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-foreground">{req.display_name}</span>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className={`text-xs font-semibold ${cfg.color}`}>{cfg.label}</span>
                <span className="text-xs text-muted-foreground/60">·</span>
                <span className="text-xs text-muted-foreground font-mono">{req.requirement_type}</span>
                {!req.is_blocking && (
                  <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 rounded border border-orange-200 dark:border-orange-800">
                    Advisory
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-muted-foreground hover:text-foreground transition-colors mt-0.5"
              data-testid={`req-expand-${idx}`}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pl-7 grid grid-cols-2 gap-2 text-xs" data-testid={`req-detail-${idx}`}>
          <div>
            <span className="text-muted-foreground">Expected</span>
            <p className="font-mono text-foreground mt-0.5">{Array.isArray(req.expected_value) ? req.expected_value.join(" or ") : req.expected_value ?? "—"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Actual</span>
            <p className={`font-mono mt-0.5 ${req.actual_value === null ? "text-muted-foreground" : "text-foreground"}`}>{req.actual_value ?? "—"}</p>
          </div>
          {req.message && (
            <div className="col-span-2">
              <span className="text-muted-foreground">Message</span>
              <p className="text-foreground mt-0.5 leading-relaxed">{req.message}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EvaluationHistoryRow({ ev, idx }) {
  const cfg = EVAL_STATUS[ev.overall_status] || EVAL_STATUS.NOT_EVALUATED;
  const Icon = cfg.icon;
  return (
    <div className="flex items-center gap-3 py-2 border-b border-border last:border-0" data-testid={`eval-history-row-${idx}`}>
      <Icon className={`h-4 w-4 shrink-0 ${cfg.color}`} />
      <div className="flex-1">
        <span className={`text-xs font-semibold ${cfg.color}`}>{ev.overall_status}</span>
        <span className="text-xs text-muted-foreground ml-2">
          {ev.satisfied_count}/{ev.required_count} satisfied · {ev.blocking_count} blocking
        </span>
      </div>
      <span className="text-xs text-muted-foreground shrink-0">
        {ev.trigger && <span className="font-mono mr-1">[{ev.trigger}]</span>}
        {new Date(ev.created_at).toLocaleString()}
      </span>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function SetupGateDetail() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { gateId } = useParams();

  const [gate, setGate] = useState(null);
  const [latestEval, setLatestEval] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  const fetchGate = useCallback(async () => {
    const token = getToken();
    if (!token || !gateId) return;
    setLoading(true);
    setError(null);
    try {
      const [gateRes, evalsRes] = await Promise.all([
        fetch(`${API}/api/setup/gates/${gateId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/setup/gates/${gateId}/evaluations?limit=5`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!gateRes.ok) throw new Error(`Gate not found (HTTP ${gateRes.status})`);
      const gateData = await gateRes.json();
      setGate(gateData);
      if (evalsRes.ok) {
        const evals = await evalsRes.json();
        setEvaluations(evals);
        setLatestEval(evals[0] || null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [gateId]);

  useEffect(() => { fetchGate(); }, [fetchGate]);

  const handleEvaluate = async () => {
    const token = getToken();
    if (!token) return;
    setEvaluating(true);
    setActionError(null);
    try {
      const res = await fetch(`${API}/api/setup/gates/${gateId}/evaluate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail?.message || err.detail || `HTTP ${res.status}`);
      }
      await fetchGate(); // refresh all data
    } catch (e) {
      setActionError(e.message);
    } finally {
      setEvaluating(false);
    }
  };

  const handleLifecycle = async (newStatus) => {
    const token = getToken();
    if (!token) return;
    setLifecycleLoading(true);
    setActionError(null);
    try {
      const res = await fetch(`${API}/api/setup/gates/${gateId}/lifecycle`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ lifecycle_status: newStatus }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail?.message || err.detail || `HTTP ${res.status}`);
      }
      await fetchGate();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setLifecycleLoading(false);
    }
  };

  const lifecycleCfg = gate ? (LIFECYCLE_STATUS[gate.lifecycle_status] || LIFECYCLE_STATUS.OPEN) : null;
  const availableActions = gate ? (LIFECYCLE_ACTIONS[gate.lifecycle_status] || []) : [];
  const evalStatus = gate?.latest_evaluation_status;

  return (
    <div className="min-h-screen bg-background" data-testid="gate-detail-page">
      {/* Header */}
      <div className="border-b border-border bg-card px-6 py-5">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                data-testid="gate-detail-back-btn"
                onClick={() => navigate("/setup/gates")}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Readiness Gates
              </button>
              {gate && (
                <>
                  <span className="text-muted-foreground/30">/</span>
                  <span className="text-xs text-foreground font-medium truncate max-w-40">{gate.gate_name}</span>
                </>
              )}
            </div>
            <button
              data-testid="gate-detail-refresh-btn"
              onClick={fetchGate}
              disabled={loading || evaluating}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2.5 py-1.5 rounded-md border border-border"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-6 py-6">

        {/* Loading */}
        {loading && (
          <div className="space-y-4" data-testid="gate-detail-loading">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-20 rounded-xl border border-border bg-muted/30 animate-pulse" />
            ))}
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="flex items-center gap-3 p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30" data-testid="gate-detail-error">
            <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            <button onClick={fetchGate} className="ml-auto text-xs text-red-600 underline">Retry</button>
          </div>
        )}

        {!loading && !error && gate && (
          <div className="space-y-6">

            {/* Gate info header */}
            <div data-testid="gate-info-section">
              <div className="flex items-start gap-3 mb-1">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${gate.gate_type === "GO_LIVE" ? "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800" : "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800"}`}>
                  {gate.gate_type.replace("_", " ")}
                </span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${lifecycleCfg?.bg} ${lifecycleCfg?.color} ${lifecycleCfg?.border}`} data-testid="gate-lifecycle-badge">
                  {gate.lifecycle_status}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-foreground mt-2">{gate.gate_name}</h1>
              <p className="text-sm font-mono text-muted-foreground mt-0.5">{gate.gate_code}</p>
              {gate.description && (
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{gate.description}</p>
              )}
              {gate.owner && (
                <p className="text-xs text-muted-foreground mt-1">Owner: <span className="text-foreground font-medium">{gate.owner}</span></p>
              )}
            </div>

            {/* Evaluation status banner */}
            <EvalStatusBanner gate={gate} />

            {/* Action error */}
            {actionError && (
              <div className="flex items-start gap-3 p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30" data-testid="gate-action-error">
                <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{actionError}</p>
                <button onClick={() => setActionError(null)} className="ml-auto text-xs text-red-500 hover:text-red-700">Dismiss</button>
              </div>
            )}

            {/* Action panel */}
            <div className="flex items-center gap-3 flex-wrap" data-testid="gate-actions">
              {/* Evaluate Now */}
              <button
                data-testid="evaluate-gate-btn"
                onClick={handleEvaluate}
                disabled={evaluating || lifecycleLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <Play className="h-4 w-4" />
                {evaluating ? "Evaluating…" : "Evaluate Now"}
              </button>

              {/* Lifecycle action buttons */}
              {availableActions.map((action) => {
                const canPerform = !action.requiresReady || evalStatus === "READY";
                return (
                  <button
                    key={action.to}
                    data-testid={`lifecycle-btn-${action.to}`}
                    onClick={() => handleLifecycle(action.to)}
                    disabled={lifecycleLoading || evaluating || !canPerform}
                    title={action.requiresReady && evalStatus !== "READY" ? "Gate must be READY before validating" : undefined}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-opacity disabled:opacity-40 ${
                      action.style === "primary"
                        ? "bg-violet-600 text-white border-violet-600 hover:bg-violet-700"
                        : action.style === "danger"
                        ? "border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                    }`}
                  >
                    {lifecycleLoading ? "…" : action.label}
                  </button>
                );
              })}
            </div>

            {/* Latest evaluation requirements */}
            {latestEval && latestEval.requirement_results && (
              <div data-testid="requirements-section">
                <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  Requirements
                  <span className="text-xs font-normal text-muted-foreground">
                    {latestEval.satisfied_count}/{latestEval.required_count} passing
                    {latestEval.blocking_count > 0 && ` · ${latestEval.blocking_count} blocking`}
                  </span>
                </h2>
                <div>
                  {latestEval.requirement_results.map((req, i) => (
                    <RequirementRow key={req.requirement_id || i} req={req} idx={i} />
                  ))}
                </div>
              </div>
            )}

            {/* No evaluation yet */}
            {!latestEval && (
              <div className="flex flex-col items-center justify-center py-10 text-center rounded-xl border border-dashed border-border" data-testid="no-eval-state">
                <Info className="h-8 w-8 text-muted-foreground/30 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">No evaluation yet</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Click "Evaluate Now" to run the first readiness check.</p>
              </div>
            )}

            {/* Evaluation history */}
            {evaluations.length > 0 && (
              <div data-testid="eval-history-section">
                <h2 className="text-sm font-semibold text-foreground mb-3">Evaluation History</h2>
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5">
                    <Info className="h-3.5 w-3.5" />
                    Immutable snapshots — historical records are never modified
                  </p>
                  {evaluations.map((ev, i) => (
                    <EvaluationHistoryRow key={ev.id} ev={ev} idx={i} />
                  ))}
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}
